import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { BadgeVariant } from '@/components/ui/badge';
import { Column, Row } from 'react-table';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import { ProgressBar } from 'react-bootstrap';
import { routes } from '../../routes';
import { TableContent } from '../components/TableContent';
import FormattedDate from '../../../../components/date/FormattedDate';
import {
  FeedbackStatusTypes,
  FeedbackStep,
} from '../../pages/PerformanceReview/Reviews/types';
import { ManagerFeedbackItem } from './mappings';
import SoftBadge from '../../components/SoftBadge';
import { EmployeesAvatarGroup } from '../../../../atomic/molecules/EmployeesAvatarGroup/EmployeeAvatarGroup';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import Hint from '../../../../components/hint';
import Button from '../../../../components/button';
import { EmptyPerformanceReview } from '../../pages/PerformanceReview/Overview/EmptyPerformanceReview';
import { AssignPeerModal } from '../../pages/PerformanceReview/EmployeePerformanceReview/AssignPeerModal/AssignPeerModal';
import {
  ManagerEmployeeReviewsDocument,
  useActiveEmployees_PeerReviewersLazyQuery,
  useCreatePeerReviewersMutation,
  useDeletePeerReviewersMutation,
} from '../../__generated__/graphql';
import FlairIcon from '../../../../atomic/atoms/FlairIcon';
import { mapAndFilterPeerOptions } from '../../pages/PerformanceReview/EmployeePerformanceReview/EmployeeEvaluation/logic';
import { useUserInfo } from '../../context/UserInfo';

type Props = {
  feedbacks: ManagerFeedbackItem[];
};

const STATUS_BADGE_VARIANT: Record<
  FeedbackStatusTypes,
  Exclude<BadgeVariant, 'transparent'>
> = {
  [FeedbackStatusTypes.NotStarted]: 'warning',
  [FeedbackStatusTypes.InProgress]: 'primary',
  [FeedbackStatusTypes.Meeting]: 'info',
  [FeedbackStatusTypes.Completed]: 'success',
};

const getReviewRoute = (item: ManagerFeedbackItem) => {
  if (item.isPeerReviewer) {
    return routes.performanceReviewerEvaluate.route;
  }
  switch (item.status) {
    case FeedbackStatusTypes.NotStarted:
    case FeedbackStatusTypes.InProgress:
      return routes.performanceReviewerEvaluate.route;
    case FeedbackStatusTypes.Meeting:
      return routes.performanceReviewerCompareFeedback.route;
    case FeedbackStatusTypes.Completed:
      return routes.performanceReviewerPreviousReviews.route;
  }
};

const getReviewUrl = (item: ManagerFeedbackItem): string => {
  const route = getReviewRoute(item);
  return route.create({ feedbackId: item.id }) + '?source=manager';
};

const getActionLabel = (
  item: ManagerFeedbackItem,
  t: (key: string) => string,
): string => {
  switch (item.status) {
    case FeedbackStatusTypes.NotStarted:
      return t('action.startReview');
    case FeedbackStatusTypes.InProgress:
      return t('action.continue');
    case FeedbackStatusTypes.Meeting:
      return t('action.startMeeting');
    case FeedbackStatusTypes.Completed:
      return t('action.view');
    default:
      return t('action.view');
  }
};

const getActionVariant = (
  item: ManagerFeedbackItem,
): 'primary' | 'outline-primary' => {
  switch (item.status) {
    case FeedbackStatusTypes.Completed:
      return 'outline-primary';
    default:
      return 'primary';
  }
};

const CLICKABLE_ROW_PROPS = { role: 'button' } as const;
const getRowProps = () => CLICKABLE_ROW_PROPS;

export const ManagerReviewsTable: React.FC<React.PropsWithChildren<Props>> = ({
  feedbacks,
}) => {
  const t = useNamespacedTranslation('performanceReview.table');
  const history = useHistory();
  const { id: currentEmployeeId } = useUserInfo();

  const [assignModalFeedbackId, setAssignModalFeedbackId] = useState<
    string | null
  >(null);

  const assignModalFeedback = useMemo(
    () => feedbacks.find((f) => f.id === assignModalFeedbackId) ?? null,
    [feedbacks, assignModalFeedbackId],
  );

  const [getActiveEmployees, { data: activeEmployeesData }] =
    useActiveEmployees_PeerReviewersLazyQuery();

  const [createPeerReviewers, { loading: loadingCreatePeer }] =
    useCreatePeerReviewersMutation({
      awaitRefetchQueries: true,
      /* eslint-disable-next-line no-restricted-syntax */
      refetchQueries: [{ query: ManagerEmployeeReviewsDocument }],
    });

  const [deletePeerReviewers, { loading: loadingDeletePeer }] =
    useDeletePeerReviewersMutation({
      awaitRefetchQueries: true,
      /* eslint-disable-next-line no-restricted-syntax */
      refetchQueries: [{ query: ManagerEmployeeReviewsDocument }],
    });

  useEffect(() => {
    if (assignModalFeedback) {
      getActiveEmployees();
    }
  }, [assignModalFeedback, getActiveEmployees]);

  const isLoadingOperations = loadingCreatePeer || loadingDeletePeer;

  const handleAssignPeer = useCallback(
    (employeeId: string) => {
      if (!assignModalFeedback) return;
      createPeerReviewers({
        variables: {
          input: {
            peerReviewerIds: [employeeId],
            outcomeId: assignModalFeedback.outcomeId,
            currentFeedbackRequestId:
              assignModalFeedback.currentFeedbackRequestId,
          },
        },
      });
    },
    [assignModalFeedback, createPeerReviewers],
  );

  const handleRemovePeer = useCallback(
    (feedbackRequestId: string, callback?: () => void) => {
      if (!assignModalFeedback) return;
      deletePeerReviewers({
        variables: {
          input: {
            currentFeedbackRequestId:
              assignModalFeedback.currentFeedbackRequestId,
            feedbackRequestIds: [feedbackRequestId],
          },
        },
      }).finally(callback);
    },
    [assignModalFeedback, deletePeerReviewers],
  );

  const handleLoadOptions = useCallback(
    (searchTerm: string) => {
      return getActiveEmployees({ variables: { searchTerm } }).then(
        (result) => {
          if (result.error) {
            throw new Error(result.error.message);
          }
          if (!result.data) {
            throw new Error('no data');
          }
          const excludeIds = [
            currentEmployeeId,
            ...(assignModalFeedback?.peerFeedbacksMapped.map(
              (p) => p.reviewer.id,
            ) ?? []),
            assignModalFeedback?.revieweeId ?? '',
          ];
          return mapAndFilterPeerOptions(
            result.data.activeEmployees,
            excludeIds,
          );
        },
      );
    },
    [getActiveEmployees, currentEmployeeId, assignModalFeedback],
  );

  const defaultPeerOptions = useMemo(() => {
    if (!activeEmployeesData || !assignModalFeedback) return [];
    const excludeIds = [
      currentEmployeeId,
      ...assignModalFeedback.peerFeedbacksMapped.map((p) => p.reviewer.id),
      assignModalFeedback.revieweeId,
    ];
    return mapAndFilterPeerOptions(
      activeEmployeesData.activeEmployees,
      excludeIds,
    );
  }, [activeEmployeesData, assignModalFeedback, currentEmployeeId]);

  const hasPeerReviews = useMemo(
    () => feedbacks.some((f) => f.withPeers),
    [feedbacks],
  );

  const columns: Column<ManagerFeedbackItem>[] = useMemo(() => {
    const cols: Column<ManagerFeedbackItem>[] = [
      {
        Header: t('header.reviewee'),
        accessor: 'reviewee',
        Cell: ({ value }: { value: string | undefined }) => (
          <span className="fw-bold">{value}</span>
        ),
      },
      {
        Header: t('header.cycle'),
        accessor: 'cycleName',
        Cell: ({
          value,
          row,
        }: {
          value: string;
          row: Row<ManagerFeedbackItem>;
        }) => <RouterLink to={getReviewUrl(row.original)}>{value}</RouterLink>,
      },
      {
        Header: t('header.dueDate'),
        accessor: 'dueDate',
        Cell: ({ value }: { value: Date | null }) =>
          value ? <FormattedDate day={value} format="short" /> : <span>-</span>,
      },
      {
        Header: t('header.status'),
        accessor: 'status',
        Cell: ({ value }: { value: FeedbackStatusTypes }) => (
          <SoftBadge variant={STATUS_BADGE_VARIANT[value]} pill>
            {t(`status.${value}`)}
          </SoftBadge>
        ),
      },
      {
        Header: t('header.steps'),
        accessor: 'steps',
        Cell: ({
          value,
          row,
        }: {
          value: FeedbackStep[];
          row: Row<ManagerFeedbackItem>;
        }) => (
          <div className="d-flex gap-1">
            {value.map((step) => (
              <div style={{ flex: 1 }} key={step.label}>
                <Hint id={row.original.id} text={t(`hintText.${step.label}`)}>
                  <ProgressBar
                    className="progress-sm"
                    max={100}
                    now={0}
                    style={{
                      backgroundColor: step.value ? 'green' : '',
                    }}
                  />
                </Hint>
              </div>
            ))}
          </div>
        ),
      },
    ];

    if (hasPeerReviews) {
      cols.push({
        Header: t('header.peers'),
        accessor: 'peerReviewers',
        Cell: ({ row }: { row: Row<ManagerFeedbackItem> }) => {
          const item = row.original;
          if (item.peerReviewers.length > 0) {
            return (
              <div className="d-flex align-items-center gap-2">
                <EmployeesAvatarGroup
                  avatarSize="xs"
                  gapSize={20}
                  recipients={item.peerReviewers}
                  maxAvatarsToShow={3}
                  showAvatarOnly={true}
                />
                {item.withPeers &&
                  item.peerReviewers.length < item.maxPeers && (
                    <FlairIcon
                      icon="add-circle-outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setAssignModalFeedbackId(item.id);
                      }}
                    />
                  )}
              </div>
            );
          }
          if (item.withPeers) {
            return (
              <Button
                size="sm"
                variant="outline-primary"
                label={t('action.select')}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  setAssignModalFeedbackId(item.id);
                }}
              />
            );
          }
          return <span>-</span>;
        },
      });
    }

    cols.push({
      Header: t('header.action'),
      id: 'action',
      Cell: ({ row }: { row: Row<ManagerFeedbackItem> }) => {
        const item = row.original;
        return (
          <RouterLink
            to={getReviewUrl(item)}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <Button
              size="sm"
              variant={getActionVariant(item)}
              label={getActionLabel(item, t)}
            />
          </RouterLink>
        );
      },
    });

    return cols;
  }, [t, hasPeerReviews]);

  if (!feedbacks.length) {
    return <EmptyPerformanceReview />;
  }

  return (
    <>
      <TableContent
        columns={columns}
        data={feedbacks}
        getRowProps={getRowProps}
        onRowClick={(row: ManagerFeedbackItem) => {
          history.push(getReviewUrl(row));
        }}
      />
      {assignModalFeedback && (
        <AssignPeerModal
          visible={true}
          onClose={() => setAssignModalFeedbackId(null)}
          onAssign={handleAssignPeer}
          onRemove={handleRemovePeer}
          maxPeers={assignModalFeedback.maxPeers}
          peerOptions={defaultPeerOptions}
          peerFeedbacks={assignModalFeedback.peerFeedbacksMapped}
          loadingOperations={isLoadingOperations}
          onLoadOptions={handleLoadOptions}
        />
      )}
    </>
  );
};
